import React from "react"
import HorizontalField, {optionalHorizontalField} from "../HorizontalField/HorizontalField"
import {UnitOrBankUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {OrgName} from "../OrgName/OrgName"
import {Accordion} from "../Accordion/Accordion"
import {
	InformationHiding,
	ssnPreview,
	phonePreview,
	emailPreview,
	datePreview,
} from "../InformationHiding/InformationHiding"
import {addressText, booleanToYesNo} from "../../utilities/helpers"
import {Resource} from "../../resources/common"
import {IndividualProductApplication} from "../../resources/productApplication"
import {CountriesOfOperation} from "../ApplicationAdditionalInformationFields/CountriesOfOperation"
import moment from "moment"

interface IndividualInformationProductApplicationProps {
	individual: IndividualProductApplication
	defaultInformationHidingState: boolean
	included?: Resource[]
}

export default function InformationIndividualProductApplication({
	individual,
	defaultInformationHidingState,
	included,
}: IndividualInformationProductApplicationProps) {
	const {attributes} = individual
	const isSoleProp = attributes.soleProprietorship

	return (
		<div className="card">
			<div className="card-header">
				<p className="card-header-title">Individual Information</p>
			</div>
			<div className="card-content">
				<UnitOrBankUserOnly>
					<HorizontalField label="Org">
						<OrgName orgId={individual.relationships.org.data.id} included={included} element={"span"} />
					</HorizontalField>
				</UnitOrBankUserOnly>
				<HorizontalField label="Application Id">
					<input type="text" className="input is-static" readOnly value={individual.id} />
				</HorizontalField>
				{individual.relationships.customer ? (
					<HorizontalField label="Customer Id">
						<input type="text" readOnly value={individual.relationships.customer.data.id} className="input is-static" />
					</HorizontalField>
				) : undefined}
				<HorizontalField label="Name">
					<input
						type="text"
						readOnly
						value={`${attributes.fullName.first} ${attributes.fullName.last}`}
						className="input is-static"
					/>
				</HorizontalField>
				{attributes.ssn ? (
					<HorizontalField label="SSN">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => attributes.ssn || ""}
							placeholder={ssnPreview(attributes.ssn)}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				) : undefined}
				{attributes.passport ? (
					<HorizontalField label="Passport">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => attributes.passport || ""}
							placeholder={ssnPreview(attributes.passport)}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				) : undefined}
				{attributes.matriculaConsular ? (
					<HorizontalField label="Matricula Consular">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => attributes.matriculaConsular || ""}
							placeholder={ssnPreview(attributes.matriculaConsular)}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				) : undefined}
				{optionalHorizontalField("Nationality", attributes.nationality)}
				{attributes.dateOfBirth && (
					<HorizontalField label="Birth Date">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => moment(attributes.dateOfBirth).format("YYYY-MM-DD")}
							placeholder={datePreview()}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				)}
				{attributes.phone ? (
					<HorizontalField label="Phone">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => `+${attributes.phone?.countryCode} ${attributes.phone?.number}`}
							placeholder={phonePreview(`+${attributes.phone?.countryCode} ${attributes.phone?.number}`)}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				) : undefined}
				{attributes.address && (
					<HorizontalField label="Address">
						<textarea
							readOnly
							value={addressText(attributes.address)}
							rows={attributes.address.street2 ? 4 : 3}
							className="textarea is-static has-fixed-size"
						/>
					</HorizontalField>
				)}
				{attributes.email ? (
					<HorizontalField label="Email">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => attributes.email || ""}
							placeholder={emailPreview(attributes.email)}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				) : undefined}
				{attributes.ein ? (
					<HorizontalField label="EIN">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => attributes.ein || ""}
							placeholder={ssnPreview(attributes.ein)}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				) : undefined}
				{optionalHorizontalField("Doing Business As", attributes.dba)}
				{attributes.soleProprietorship !== undefined && attributes.soleProprietorship !== null ? (
					<HorizontalField label="Sole Proprietorship">
						<input
							type="text"
							readOnly
							value={attributes.soleProprietorship ? "Yes" : "No"}
							className="input is-static"
						/>
					</HorizontalField>
				) : undefined}
				<Accordion title={"Additional Information"} theme={"light"}>
					{optionalHorizontalField("Occupation", attributes.occupation)}
					{optionalHorizontalField("Annual Income", attributes.annualIncome)}
					{optionalHorizontalField("Source of Income", attributes.sourceOfIncome)}
					{optionalHorizontalField("Business Vertical", attributes.businessVertical)}
					{optionalHorizontalField("Website", attributes.website)}
					{optionalHorizontalField("Number of Employees", attributes.numberOfEmployees)}
					{optionalHorizontalField("Annual Revenue", attributes.annualRevenue)}
					{optionalHorizontalField("Source of Funds", attributes.sourceOfFunds)}
					{optionalHorizontalField("Source of Funds Description", attributes.sourceOfFundsDescription)}
					{optionalHorizontalField("Account Purpose", attributes.accountPurpose)}
					{optionalHorizontalField("Account Purpose Detail", attributes.accountPurposeDetail)}
					{optionalHorizontalField("Transaction Volume", attributes.transactionVolume)}
					{optionalHorizontalField("Transaction Volume Description", attributes.transactionVolumeDescription)}
					{!isSoleProp && optionalHorizontalField("Profession", attributes.profession)}
					{isSoleProp && optionalHorizontalField("Business Industry", attributes.businessIndustry)}
					{isSoleProp && attributes.usNexus && optionalHorizontalField("US Nexus", attributes.usNexus.join(", "))}
					{isSoleProp && optionalHorizontalField("Is Incorporated", booleanToYesNo(attributes.isIncorporated))}
					{isSoleProp && optionalHorizontalField("State of Incorporation", attributes.stateOfIncorporation)}
					{isSoleProp && optionalHorizontalField("Year of Incorporation", attributes.yearOfIncorporation)}
					{isSoleProp && (
						<CountriesOfOperation countriesOfOperation={attributes.countriesOfOperation} isArchived={false} />
					)}
					{isSoleProp && optionalHorizontalField("Business Description", attributes.businessDescription)}
				</Accordion>
			</div>
		</div>
	)
}
